import '@/styles/tailwind-core.module.css';
import { Bakbak_One, Suez_One } from 'next/font/google';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';

import Footer from '@/common/footer';
import Header from '@/common/header';
import NextSeoComponent from '@/layouts/next-seo-component';
import logo from '@/public/assets/images/game/dota2/dota2_logo.webp';
import imageStepOne from '@/public/assets/images/game/dota2/step1.webp';
import imageStepTwo from '@/public/assets/images/game/dota2/step2.webp';
import imageStepThree from '@/public/assets/images/game/dota2/step3.webp';
import { Main } from '@/templates/main';
import { buildCanonical } from '@/utils/seo';

const suezOne = Suez_One({
  subsets: ['latin'],
  weight: '400',
  display: 'swap',
  variable: '--font-suezOne',
});

export const bakbakOne = Bakbak_One({
  subsets: ['latin'],
  weight: '400',
  display: 'swap',
  variable: '--font-bakBakOne',
});

export default function DotaPage() {
  const router = useRouter();
  const { t } = useTranslation(['pages/dota', 'components/titles']);

  return (
    <>
      <Header variant='primary' />

      <Main meta={<NextSeoComponent title={t('dota2', { ns: 'components/titles' })} description={t('dota.description')} canonical={buildCanonical(router.locale, '/dota2')} structuredDataType='software' breadcrumbs={[{ name: t('dota2', { ns: 'components/titles' }), item: buildCanonical(router.locale, '/dota2') }]} />} className="bg-[url('/assets/images/game/dota2/background.webp')] bg-cover bg-top bg-no-repeat">
        <div className='flex justify-center p-5 xl:bg-center xl:pb-40 xl:pt-20'>
          <div className='flex w-full max-w-[1487px] gap-5 xl:gap-14'>
            <div className='hidden flex-col items-center gap-11 xl:flex'>
              <Image src={logo} alt='Dota 2' />
              <div className='flex h-[6.5rem] w-1 bg-[#FF6046]' />
            </div>

            <div className='mt-5 xl:mt-24'>
              <h1 className={`mb-5 max-w-[460px] ${suezOne.variable} font-suez_one text-[40px] font-normal leading-12 text-white xl:mb-16`}>{t('dota.title')}</h1>

              <figure className='mb-5 xl:mb-12'>
                {/* eslint-disable-next-line jsx-a11y/media-has-caption -- the steps below (#how-to-steps) are the text alternative */}
                <video preload='none' width='600' controls muted aria-describedby='how-to-steps'>
                  <source src='/assets/video/dota-2/video.mp4' type='video/mp4' />
                </video>
                <figcaption className='mt-2 max-w-[600px] font-bai_jamjuree text-sm font-medium text-white/90'>{t('dota.video_caption')}</figcaption>
              </figure>
              <div className='flex items-start justify-start gap-4'>
                <span className='size-4 min-h-4 min-w-4 bg-[#EE3537]' />
                <div className='flex flex-col gap-5'>
                  <p className='w-full max-w-[528px] font-bai_jamjuree text-base font-medium text-white'>{t('dota.description')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className='flex justify-center p-5 xl:pb-28 xl:pt-11'>
          <div className='flex w-full max-w-[1487px] gap-5 xl:gap-14'>
            <div id='how-to-steps' className='grid grid-cols-1 gap-5 xl:grid-cols-3 xl:gap-40'>
              <div className='flex w-full flex-col xl:max-w-[372px]'>
                <div className='mb-5 flex items-center justify-start gap-4'>
                  <span className='size-sm min-h-3.5 min-w-3.5 bg-[#EE3537]' />
                  <p className={`w-full max-w-[528px] ${bakbakOne.variable} font-bakbak_one text-xl font-normal leading-7 text-[#EE3537]`}>1</p>
                </div>

                <Image className='mb-5' src={imageStepOne} alt={t('dota.first_step_image_alt')} quality={100} />
                <p className='w-full max-w-[528px] font-bai_jamjuree text-base font-bold leading-5 text-[#EE3537]'>{t('dota.first_step_title')}</p>

                <div className='my-5 flex items-start justify-start gap-4'>
                  <span className='size-sm min-h-3.5 min-w-3.5 bg-[#EE3537]' />
                  <p className='w-full max-w-[528px] font-bai_jamjuree text-base font-medium leading-5 text-white'>{t('dota.first_step_text')}</p>
                </div>
              </div>

              <div className='flex w-full flex-col xl:max-w-[372px]'>
                <div className='mb-5 flex items-center justify-start gap-4'>
                  <span className='size-sm min-h-3.5 min-w-3.5 bg-[#EE3537]' />
                  <p className={`w-full max-w-[528px] ${bakbakOne.variable} font-bakbak_one text-xl font-normal leading-7 text-[#EE3537]`}>2</p>
                </div>

                <Image className='mb-5' src={imageStepTwo} alt={t('dota.second_step_image_alt')} quality={100} />
                <p className='mb-5 w-full max-w-[528px] font-bai_jamjuree text-base font-bold leading-5 text-[#EE3537]'>{t('dota.second_step_title')}</p>

                <div className='my-5 flex items-start justify-start gap-4'>
                  <span className='size-sm min-h-3.5 min-w-3.5 bg-[#EE3537]' />
                  <p className='w-full max-w-[528px] font-bai_jamjuree text-base font-medium leading-5 text-white'>{t('dota.second_step_text')}</p>
                </div>
              </div>

              <div className='flex w-full flex-col xl:max-w-[372px]'>
                <div className='mb-5 flex items-center justify-start gap-4'>
                  <span className='size-sm min-h-3.5 min-w-3.5 bg-[#EE3537]' />
                  <p className={`w-full max-w-[528px] ${bakbakOne.variable} font-bakbak_one text-xl font-normal leading-7 text-[#EE3537]`}>3</p>
                </div>

                <Image className='mb-5' src={imageStepThree} alt={t('dota.third_step_image_alt')} quality={100} />
                <p className='mb-5 w-full max-w-[528px] font-bai_jamjuree text-base font-bold leading-5 text-[#EE3537]'>{t('dota.third_step_title')}</p>

                <div className='my-5 flex items-start justify-start gap-4'>
                  <span className='size-sm min-h-3.5 min-w-3.5 bg-[#EE3537]' />
                  <p className='w-full max-w-[528px] font-bai_jamjuree text-base font-medium leading-5 text-white'>{t('dota.third_step_text')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Main>
      <Footer />
    </>
  );
}

export async function getStaticProps({ locale }: { locale: string }) {
  return {
    props: {
      ...(await serverSideTranslations(locale, ['common', 'pages/dota', 'components/titles'])),
    },
  };
}
